import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Interface for milestone data
 */
export interface TicketMilestone {
  id: number;
  name: string;
  status: 'upcoming' | 'in-progress' | 'completed' | 'overdue';
  dueDate?: string | Date;
  progress?: number;
  daysRemaining?: number;
}

/**
 * TicketMainContentComponent
 *
 * Main content area component for ticket dialog.
 * Features:
 * - Ticket name input (hidden in view mode)
 * - Description editor with @mentions support
 * - Milestone card display
 * - Tab group for details, checklist, attachments, comments, history
 *
 * @selector app-ticket-main-content
 */
@Component({
    selector: 'app-ticket-main-content',
    templateUrl: './ticket-main-content.component.html',
    styleUrls: ['./ticket-main-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TicketMainContentComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  // Destroy subject for unsubscribing
  private destroy$ = new Subject<void>();

  // =============================================================================
  // INTERMEDIATE FORM CONTROLS (Required for Syncfusion mentions to work)
  // =============================================================================

  /**
   * Intermediate form control for ticket name
   * Syncs between parent form and component
   */
  public ticketNameControl!: FormControl;

  /**
   * Intermediate form control for description
   * Required for Syncfusion mentions to properly track changes
   */
  public ticketDescriptionControl!: FormControl;

  // =============================================================================
  // INPUTS
  // =============================================================================

  /**
   * Mode: create, edit, or view
   */
  @Input() mode: 'create' | 'edit' | 'view' = 'create';

  /**
   * Ticket form group
   */
  @Input() ticketForm!: FormGroup;

  /**
   * Ticket title from form
   */
  @Input() ticketTitle: string = '';

  /**
   * Description from form
   */
  @Input() description: string = '';

  /**
   * Selected milestone (if any)
   */
  @Input() selectedMilestone: TicketMilestone | null = null;

  /**
   * Lock state - when true, editing is disabled
   */
  @Input() isLocked: boolean = false;

  /**
   * Tab badge counts
   */
  @Input() detailsCount: number = 0;
  @Input() checklistCount: number = 0;
  @Input() materialCount: number = 0;
  @Input() attachmentCount: number = 0;
  @Input() commentCount: number = 0;
  @Input() historyCount: number = 0;

  /**
   * Active tab index
   */
  @Input() activeTabIndex: number = 0;

  /**
   * Hide tabs that are only relevant once ticket exists
   * (Details, Comments, History)
   */
  @Input() hideDetailsCommentsHistory: boolean = false;

  /**
   * Materials & Services only applies to tickets raised on a Job — every other
   * module has no scope items to draw from, so the tab is not rendered at all.
   */
  @Input() showMaterials: boolean = false;

  /**
   * Read-only flag
   */
  @Input() isReadOnly: boolean = false;

  /**
   * Mentions array for @mention functionality
   */
  @Input() mentions: any[] = [];

  /**
   * Mentions loading state
   */
  @Input() isMentionsLoading: boolean = false;

  // =============================================================================
  // OUTPUTS
  // =============================================================================

  /**
   * Emits when title changes
   */
  @Output() titleChange = new EventEmitter<string>();

  /**
   * Emits when description changes
   */
  @Output() descriptionChange = new EventEmitter<string>();

  /**
   * Emits when tab changes
   */
  @Output() tabChange = new EventEmitter<number>();

  /**
   * The active tab's stable key. mat-tab-group numbers tabs by RENDER order, so
   * hiding Details/Comments/History (create mode) or Materials (non-job ticket)
   * shifts every index after it. Parents that need to know "is Comments open"
   * must not hardcode a number — they listen for the key instead.
   */
  @Output() tabKeyChange = new EventEmitter<string>();

  /**
   * Emits when milestone card is clicked
   */
  @Output() milestoneClick = new EventEmitter<void>();

  /**
   * Mention feature outputs
   */
  @Output() mentionSelected = new EventEmitter<any>();
  @Output() mentionSearch = new EventEmitter<string>();

  // =============================================================================
  // VIEW CHILDREN
  // =============================================================================

  /**
   * Description target div reference
   */
  @ViewChild('descriptionTarget') descriptionTarget!: ElementRef;

  // =============================================================================
  // PUBLIC PROPERTIES
  // =============================================================================

  /**
   * Tab labels with counts
   */
  public tabs = [
    { label: 'Details', icon: 'info', count: 0 },
    { label: 'Checklist', icon: 'checklist', count: 0 },
    { label: 'Materials & Services', icon: 'inventory_2', count: 0 },
    { label: 'Attachments', icon: 'attach_file', count: 0 },
    { label: 'Comments', icon: 'comment', count: 0 },
    { label: 'History', icon: 'history', count: 0 },
  ];

  /**
   * Custom mention dropdown state
   */
  public showMentionDropdown: boolean = false;
  public mentionDropdownTop: number = 0;
  public mentionDropdownLeft: number = 0;
  public selectedMentionIndex: number = 0;
  private currentMentionSearch: string = '';
  private mentionStartPos: number = 0;
  private clickOutsideListener?: (event: MouseEvent) => void;

  // =============================================================================
  // CONSTRUCTOR
  // =============================================================================

  constructor(private cdr: ChangeDetectorRef) {
    // Initialize intermediate form controls with validators (copied from task module)
    this.ticketNameControl = new FormControl('', [Validators.required]);
    this.ticketDescriptionControl = new FormControl('');
  }

  // =============================================================================
  // LIFECYCLE HOOKS
  // =============================================================================

  ngOnInit(): void {
    // DEBUG: Verify mode and readonly state
    console.log('[TICKET-MAIN-CONTENT] ngOnInit - mode:', this.mode, 'isReadOnly:', this.isReadOnly);
    console.log('[TICKET-MAIN-CONTENT] ngOnInit - mentions array length:', this.mentions?.length || 0);
    console.log('[TICKET-MAIN-CONTENT] ngOnInit - mentions data:', this.mentions);

    this.updateTabCounts();
    this.syncFormControls();
  }

  /**
   * Sync form controls with component state (copied EXACTLY from task module)
   * Sets up bidirectional synchronization between parent form and intermediate controls
   */
  private syncFormControls(): void {
    if (!this.ticketForm) {
      return;
    }

    // Sync ticket title
    const titleControl = this.ticketForm.get('title');
    if (titleControl) {
      this.ticketNameControl.setValue(titleControl.value, { emitEvent: false });

      // Subscribe to changes - bidirectional sync with cleanup
      this.ticketNameControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          titleControl.setValue(value, { emitEvent: false });
        });

      titleControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.ticketNameControl.setValue(value, { emitEvent: false });
        });
    }

    // Sync description
    const descriptionControl = this.ticketForm.get('description');
    if (descriptionControl) {
      this.ticketDescriptionControl.setValue(descriptionControl.value, {
        emitEvent: false,
      });

      // NOTE: DO NOT set innerHTML here! Syncfusion needs to initialize first.
      // Initial rendering will be handled by setDescriptionContent() from parent.

      // Subscribe to changes - bidirectional sync with cleanup
      this.ticketDescriptionControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          descriptionControl.setValue(value, { emitEvent: false });
        });

      descriptionControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.ticketDescriptionControl.setValue(value, { emitEvent: false });
          // Update DOM when form control changes
          if (this.descriptionTarget) {
            this.descriptionTarget.nativeElement.innerHTML = value || '';
          }
        });
    }
  }

  ngAfterViewInit(): void {
    // DEBUG: Verify ViewChildren are initialized
    console.log('[TICKET-MAIN-CONTENT] ngAfterViewInit - descriptionTarget initialized:', !!this.descriptionTarget);

    // Setup mention chip protection
    this.setupMentionChipProtection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateTabCounts();

    // Re-sync form controls when ticketForm changes (matches task module pattern)
    if (changes['ticketForm']) {
      this.syncFormControls();
    }

    // DEBUG: Log when mentions array changes
    if (changes['mentions']) {
      console.log('[TICKET-MAIN-CONTENT] ngOnChanges - mentions changed:', {
        previousValue: changes['mentions'].previousValue?.length || 0,
        currentValue: changes['mentions'].currentValue?.length || 0,
        mentions: this.mentions
      });
    }

    // Note: Mentions dataSource is managed by Angular's [dataSource]="mentions" binding
    // No manual refresh needed - the task module proves this approach works perfectly
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeClickOutsideListener();
  }

  /**
   * Render description HTML in the editor
   */
  private renderDescription(description: string): void {
    if (!description || !this.descriptionTarget) {
      return;
    }

    // Check if it's plain text (no HTML tags)
    if (!/<[^>]+>/.test(description)) {
      // Wrap in paragraph tag and replace newlines with <br>
      const htmlContent = `<p>${description.replace(/\n/g, '<br>')}</p>`;
      this.descriptionTarget.nativeElement.innerHTML = htmlContent;
    } else {
      this.descriptionTarget.nativeElement.innerHTML = description;
    }
  }

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  /**
   * Handle keyup events in description for custom @ mention detection
   * @param event Keyboard event
   */
  public onDescriptionKeyup(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const text = target.textContent || '';
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) return;

    const cursorPos = this.getCaretPosition(target);

    // Check for @ symbol
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

      // Check if there's a space after @, if so, close dropdown
      if (textAfterAt.includes(' ')) {
        this.closeMentionDropdown();
        return;
      }

      // Show dropdown with search query
      this.currentMentionSearch = textAfterAt;
      this.mentionStartPos = lastAtIndex;

      // Calculate dropdown position
      const rect = this.getCaretCoordinates();
      this.mentionDropdownTop = rect.top;
      this.mentionDropdownLeft = rect.left;

      this.showMentionDropdown = true;
      this.selectedMentionIndex = 0;

      // Setup click outside listener
      this.setupClickOutsideListener();

      // Trigger search
      this.mentionSearch.emit(textAfterAt);
    } else {
      this.closeMentionDropdown();
    }

    // Handle arrow keys and Enter in dropdown
    if (this.showMentionDropdown) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.selectedMentionIndex = Math.min(this.selectedMentionIndex + 1, this.mentions.length - 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.selectedMentionIndex = Math.max(this.selectedMentionIndex - 1, 0);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.mentions[this.selectedMentionIndex]) {
          this.selectMention(this.mentions[this.selectedMentionIndex]);
        }
      } else if (event.key === 'Escape') {
        this.closeMentionDropdown();
      }
    }
  }

  /**
   * Get caret position in contenteditable div
   */
  private getCaretPosition(element: HTMLElement): number {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return preCaretRange.toString().length;
  }

  /**
   * Get caret coordinates for dropdown positioning (viewport coordinates)
   */
  private getCaretCoordinates(): {top: number, left: number, height: number} {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return { top: 0, left: 0, height: 20 };
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // Base viewport coordinates (for fixed positioning)
    let top = rect.bottom + window.scrollY; // Position below cursor
    let left = rect.left + window.scrollX;

    // -----------------------------------------------------------------------
    // Clamp dropdown horizontally so it never overflows off the right edge
    // -----------------------------------------------------------------------
    // Keep behaviour same as task/note modules so UX is consistent.
    const DROPDOWN_WIDTH = 280; // px – should roughly match .custom-mention-dropdown width
    const PADDING = 16;         // px – distance from viewport edge
    const viewportWidth = window.innerWidth;

    const maxLeft = viewportWidth - DROPDOWN_WIDTH - PADDING;
    if (left > maxLeft) {
      left = Math.max(PADDING, maxLeft);
    }

    return {
      top,
      left,
      height: rect.height || 20
    };
  }

  /**
   * Select a mention from dropdown
   * IMPROVED: Simple and reliable - preserves all existing mention chips
   */
  public selectMention(user: any): void {
    const target = this.descriptionTarget.nativeElement;
    const currentHTML = target.innerHTML;

    // Create the new mention chip HTML
    const mentionText = `@${user.full_name}`;
    const searchText = `@${this.currentMentionSearch}`;
    const mentionHTML = `<span class="e-mention-chip" data-mention-value="${user.id}" contenteditable="false">${mentionText}</span>`;

    // Simple approach: Find the search text in HTML and replace it
    // This preserves all existing chips because we're doing a targeted replacement

    // Get the position in the plain text
    const plainText = target.textContent || '';
    const searchStartPos = this.mentionStartPos;
    const searchEndPos = searchStartPos + searchText.length;

    // Build the replacement by working with innerHTML segments
    let newHTML = currentHTML;
    let replacementDone = false;

    // Method 1: Try direct replacement if search text exists in HTML
    if (currentHTML.includes(searchText)) {
      // Find the last occurrence (most recent typed)
      const lastIndex = currentHTML.lastIndexOf(searchText);

      // Make sure we're not replacing inside a chip
      const beforeSearchHTML = currentHTML.substring(0, lastIndex);
      const afterSearchHTML = currentHTML.substring(lastIndex + searchText.length);

      // Check if we're inside a chip by looking for unclosed chip tags
      const openChipCount = (beforeSearchHTML.match(/<span[^>]*class="[^"]*e-mention-chip[^"]*"/g) || []).length;
      const closeChipCount = (beforeSearchHTML.match(/<\/span>/g) || []).length;

      if (openChipCount === closeChipCount) {
        // We're not inside a chip, safe to replace
        newHTML = beforeSearchHTML + mentionHTML + '&nbsp;' + afterSearchHTML;
        replacementDone = true;
      }
    }

    // Method 2: If direct replacement didn't work, walk through the DOM
    if (!replacementDone) {
      newHTML = this.replaceSearchTextInHTML(currentHTML, searchText, mentionHTML, this.mentionStartPos);
    }

    // Set the new HTML
    target.innerHTML = newHTML;

    // Place cursor after the new mention
    setTimeout(() => {
      // Find the newly inserted chip and place cursor after it
      const allChips = target.querySelectorAll('.e-mention-chip');
      if (allChips.length > 0) {
        const lastChip = allChips[allChips.length - 1] as HTMLElement;
        this.moveCursorAfterElement(lastChip);
      }
    }, 10);

    // Extract and emit ALL mentioned users
    setTimeout(() => {
      this.extractAndEmitMentionedUsers(newHTML);

      // Update form control with plain text
      const newPlainText = this.extractPlainText(newHTML);
      this.ticketDescriptionControl.setValue(newPlainText, { emitEvent: false });

      const descriptionControl = this.ticketForm?.get('description');
      if (descriptionControl) {
        descriptionControl.setValue(newPlainText, { emitEvent: false });
      }
    }, 50);

    // Close dropdown
    this.closeMentionDropdown();
  }

  /**
   * Replace search text in HTML while preserving existing chips
   * Walks through the HTML character by character
   */
  private replaceSearchTextInHTML(html: string, searchText: string, replacement: string, targetPosition: number): string {
    // Parse HTML to work with DOM
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Walk through text nodes to find the target position
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);
    let currentPos = 0;
    let targetNode: Text | null = null;
    let positionInNode = 0;

    let node;
    while (node = walker.nextNode()) {
      const textNode = node as Text;
      const text = textNode.textContent || '';
      const len = text.length;

      if (currentPos <= targetPosition && targetPosition < currentPos + len) {
        // Found the node containing our target position
        targetNode = textNode;
        positionInNode = targetPosition - currentPos;
        break;
      }

      currentPos += len;
    }

    if (targetNode) {
      const text = targetNode.textContent || '';
      const beforeText = text.substring(0, positionInNode);
      const afterText = text.substring(positionInNode + searchText.length);

      // Create replacement nodes
      const fragment = document.createDocumentFragment();

      if (beforeText) {
        fragment.appendChild(document.createTextNode(beforeText));
      }

      // Parse the replacement HTML
      const tempSpan = document.createElement('span');
      tempSpan.innerHTML = replacement + '&nbsp;';
      while (tempSpan.firstChild) {
        fragment.appendChild(tempSpan.firstChild);
      }

      if (afterText) {
        fragment.appendChild(document.createTextNode(afterText));
      }

      // Replace the text node
      targetNode.parentNode?.replaceChild(fragment, targetNode);
    }

    return tempDiv.innerHTML;
  }

  /**
   * Handle description content change (blur event)
   * @param event Blur event from contenteditable div
   */
  onDescriptionChange(event: any): void {
    const htmlContent = event.target.innerHTML;

    // Extract plain text for form control
    const plainText = this.extractPlainText(htmlContent);

    // Sync to parent form control
    const descriptionControl = this.ticketForm?.get('description');
    if (descriptionControl) {
      descriptionControl.setValue(plainText, { emitEvent: false });
    }

    // Emit to parent
    this.descriptionChange.emit(htmlContent);

    // Extract mentioned user IDs
    this.extractMentionedUsers(htmlContent);
  }

  /**
   * Handle description input (real-time updates)
   * @param event Input event from contenteditable div
   */
  onDescriptionInput(event: any): void {
    console.log('⌨️ [DESCRIPTION-INPUT] User is typing');

    const htmlContent = event.target.innerHTML;
    const plainText = this.extractPlainText(htmlContent);

    console.log('⌨️ [DESCRIPTION-INPUT] Plain text:', plainText);

    // Sync to intermediate control
    this.ticketDescriptionControl.setValue(plainText, { emitEvent: false });

    // Sync to parent form control
    const descriptionControl = this.ticketForm?.get('description');
    if (descriptionControl) {
      descriptionControl.setValue(plainText, { emitEvent: false });
    }

    // Extract and emit all mentioned users whenever content changes
    this.extractAndEmitMentionedUsers(htmlContent);
  }

  /**
   * Handle tab selection changes
   */
  onTabChange(index: number): void {
    this.tabChange.emit(index);
    this.tabKeyChange.emit(this.renderedTabKeys[index] ?? '');
  }

  /**
   * The tabs actually rendered, in render order — the single source of truth for
   * index ↔ meaning. Anything that needs to know which tab is open reads this
   * rather than assuming a fixed position.
   */
  get renderedTabKeys(): string[] {
    const keys: string[] = [];

    if (!this.hideDetailsCommentsHistory) { keys.push('details'); }
    keys.push('checklist');
    if (this.showMaterials) { keys.push('materials'); }
    keys.push('attachments');
    if (!this.hideDetailsCommentsHistory) { keys.push('comments', 'history'); }

    return keys;
  }

  /**
   * Handle milestone card click
   */
  onMilestoneCardClick(): void {
    this.milestoneClick.emit();
  }

  // =============================================================================
  // HELPER METHODS
  // =============================================================================

  /**
   * Update tab counts from inputs
   */
  private updateTabCounts(): void {
    this.tabs[0].count = this.detailsCount;
    this.tabs[1].count = this.checklistCount;
    this.tabs[2].count = this.materialCount;
    this.tabs[3].count = this.attachmentCount;
    this.tabs[4].count = this.commentCount;
    this.tabs[5].count = this.historyCount;
  }

  /**
   * Get milestone status badge class
   */
  getMilestoneStatusClass(): string {
    if (!this.selectedMilestone) return '';
    const status = this.selectedMilestone.status;
    return `milestone-status-${status}`;
  }

  /**
   * Get milestone progress width
   */
  getMilestoneProgressWidth(): string {
    return `${this.selectedMilestone?.progress || 0}%`;
  }

  /**
   * Get days remaining text
   */
  getDaysRemainingText(): string {
    const days = this.selectedMilestone?.daysRemaining;
    if (!days) return '';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  }

  /**
   * Format milestone due date
   */
  formatMilestoneDueDate(): string {
    if (!this.selectedMilestone?.dueDate) return '';
    const date = new Date(this.selectedMilestone.dueDate);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Extract plain text from HTML content
   * @param html HTML content string
   * @returns Plain text string
   */
  private extractPlainText(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  /**
   * Extract mentioned user IDs from HTML content
   * @param html HTML content with mention spans
   */
  private extractMentionedUsers(html: string): void {
    this.extractAndEmitMentionedUsers(html);
  }

  /**
   * Extract and emit ALL mentioned users from HTML content
   * This collects all mention chips and sends them to parent
   * @param html HTML content with mention spans
   */
  private extractAndEmitMentionedUsers(html: string): void {
    // Parse HTML to find all mention chips
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const mentionChips = doc.querySelectorAll('.e-mention-chip');

    const mentionedUsers: any[] = [];
    const uniqueUserIds = new Set<number>();

    mentionChips.forEach((chip) => {
      const userId = chip.getAttribute('data-mention-value');
      const userName = chip.textContent?.replace('@', '').trim();

      if (userId) {
        const userIdNum = parseInt(userId, 10);
        // Only add if not already in set (avoid duplicates)
        if (!uniqueUserIds.has(userIdNum)) {
          uniqueUserIds.add(userIdNum);
          mentionedUsers.push({
            id: userIdNum,
            name: userName || '',
            full_name: userName || ''
          });
        }
      }
    });

    console.log('[CUSTOM MENTION] Extracted mentioned users:', mentionedUsers);

    // Always emit the full array of mentioned users (even if empty)
    this.mentionSelected.emit(mentionedUsers);
  }

  /**
   * Set description content with mentions (for edit mode)
   * Converts plain @mentions to Syncfusion mention chips
   * @param text Plain text description
   * @param mentionedUsers Array of mentioned users
   *
   * NOTE: Copied EXACTLY from task module (working implementation)
   */
  public setDescriptionContent(text: string, mentionedUsers: any[] = []): void {
    console.log('[SET-DESCRIPTION] Called with:', {
      text: text,
      mentionedUsersCount: mentionedUsers?.length || 0,
      mentionedUsers: mentionedUsers,
      hasDescriptionTarget: !!this.descriptionTarget
    });

    if (!this.descriptionTarget) {
      console.warn('[SET-DESCRIPTION] ⚠️ descriptionTarget not available!');
      return;
    }

    let html = text;

    // Replace plain @mentions with styled spans
    mentionedUsers.forEach((user) => {
      const fullName = user.full_name || user.name;
      const mentionText = `@${fullName}`;

      console.log('[SET-DESCRIPTION] Processing mention:', {
        fullName,
        mentionText,
        userId: user.id
      });

      // Escape special regex characters in the mention text
      const escapedMention = mentionText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      const mentionHtml = `<span class="e-mention-chip" data-mention-value="${user.id}">${mentionText}</span>`;
      html = html.replace(new RegExp(escapedMention, 'g'), mentionHtml);
    });

    console.log('[SET-DESCRIPTION] Final HTML:', html);
    this.descriptionTarget.nativeElement.innerHTML = html;

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Setup click outside listener to close dropdown
   */
  private setupClickOutsideListener(): void {
    // Remove existing listener if any
    this.removeClickOutsideListener();

    // Add new listener after a small delay to avoid immediate closure
    setTimeout(() => {
      this.clickOutsideListener = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const dropdownEl = document.querySelector('.custom-mention-dropdown');
        const descriptionEl = this.descriptionTarget?.nativeElement;

        // Check if click is outside both dropdown and description field
        if (dropdownEl && !dropdownEl.contains(target) &&
            descriptionEl && !descriptionEl.contains(target)) {
          this.closeMentionDropdown();
          this.cdr.detectChanges();
        }
      };

      document.addEventListener('click', this.clickOutsideListener as any);
    }, 100);
  }

  /**
   * Remove click outside listener
   */
  private removeClickOutsideListener(): void {
    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener as any);
      this.clickOutsideListener = undefined;
    }
  }

  /**
   * Close mention dropdown
   */
  private closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    this.currentMentionSearch = '';
    this.selectedMentionIndex = 0;
    this.removeClickOutsideListener();
  }

  /**
   * Setup protection to prevent cursor from entering mention chips
   * Moves cursor outside chip when user tries to edit inside it
   */
  private setupMentionChipProtection(): void {
    setTimeout(() => {
      if (!this.descriptionTarget) return;

      const target = this.descriptionTarget.nativeElement;

      // Handle click events on chips
      target.addEventListener('click', (event: MouseEvent) => {
        const clickedElement = event.target as HTMLElement;

        // Check if clicked on a mention chip
        if (clickedElement.classList.contains('e-mention-chip') ||
            clickedElement.closest('.e-mention-chip')) {
          const chip = clickedElement.classList.contains('e-mention-chip')
            ? clickedElement
            : clickedElement.closest('.e-mention-chip');

          if (chip) {
            // Move cursor after the chip
            this.moveCursorAfterElement(chip as HTMLElement);
            event.preventDefault();
            event.stopPropagation();
          }
        }
      });

      // Handle keydown to prevent editing inside chips
      target.addEventListener('keydown', (event: KeyboardEvent) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        let node = range.startContainer;

        // Check if cursor is inside a chip
        while (node && node !== target) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.classList.contains('e-mention-chip')) {
              // Cursor is inside chip, move it outside
              if (event.key === 'Backspace' || event.key === 'Delete') {
                // Allow deletion of entire chip
                element.remove();
                event.preventDefault();
                return;
              } else if (event.key === 'ArrowLeft') {
                this.moveCursorBeforeElement(element);
                event.preventDefault();
                return;
              } else if (event.key === 'ArrowRight') {
                this.moveCursorAfterElement(element);
                event.preventDefault();
                return;
              } else {
                // For other keys, move cursor after chip
                this.moveCursorAfterElement(element);
                return; // Let the key be processed after cursor move
              }
            }
          }
          node = node.parentNode as Node;
        }
      });
    }, 100);
  }

  /**
   * Move cursor before an element
   */
  private moveCursorBeforeElement(element: HTMLElement): void {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStartBefore(element);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  /**
   * Move cursor after an element
   */
  private moveCursorAfterElement(element: HTMLElement): void {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStartAfter(element);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);

    // Insert a zero-width space to ensure cursor has somewhere to go
    const textNode = document.createTextNode('\u200B');
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);
  }
}
